import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneCall, Search, Loader2, PhoneForwarded, RefreshCw, UserRound, Unplug, ShieldAlert, CheckCircle2, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { StatCard } from "@/components/admin/_shared";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");

function Caps({ caps }) {
  const items = [
    ["Voice", caps?.voice], ["SMS", caps?.sms], ["MMS", caps?.mms], ["WhatsApp", caps?.whatsapp],
  ];
  return (
    <div className="flex flex-wrap gap-1">
      {items.map(([label, on]) => (
        <span key={label} className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${on ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
          {label}{!on && label === "WhatsApp" ? " —" : ""}
        </span>
      ))}
    </div>
  );
}

export default function AdminTwilioNumbers() {
  const navigate = useNavigate();
  const [data, setData] = useState({ available: [], assigned: [], configured: true });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [customers, setCustomers] = useState([]);
  const [assignDlg, setAssignDlg] = useState(null); // {number} assigning an available number
  const [replaceDlg, setReplaceDlg] = useState(null); // {row} replacing an assigned customer's number
  const [pickCustomer, setPickCustomer] = useState("");
  const [pickNumber, setPickNumber] = useState("");
  const [custQ, setCustQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/twilio/number-management");
      setData(r.data);
    } catch { toast.error("Could not load Twilio numbers"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    adminApi.get("/admin/customers", { params: { page: 1, page_size: 1000 } })
      .then((r) => setCustomers(r.data.customers || r.data.rows || [])).catch(() => {});
  }, []);

  const filterRow = (r, isAssigned) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [r.phone_number, r.friendly_name, r.country, isAssigned ? r.customer_name : "", isAssigned ? r.customer_email : "", isAssigned ? r.business_name : ""]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  };

  const available = useMemo(() => data.available.filter((r) => filterRow(r, false)), [data, q]);
  const assigned = useMemo(() => data.assigned.filter((r) => filterRow(r, true)), [data, q]);

  const filteredCustomers = useMemo(() => {
    const s = custQ.toLowerCase();
    return customers.filter((c) => !s || `${c.name || ""} ${c.email || ""} ${c.business_name || ""}`.toLowerCase().includes(s)).slice(0, 40);
  }, [customers, custQ]);

  const doAssign = async () => {
    if (!pickCustomer) { toast.error("Choose a customer"); return; }
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/twilio/reassign", { user_id: pickCustomer, phone_number: assignDlg.number });
      toast.success(r.data.message || "Number assigned");
      setAssignDlg(null); setPickCustomer(""); setCustQ("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not assign number"); }
    finally { setBusy(false); }
  };

  const doReplace = async () => {
    if (!pickNumber) { toast.error("Choose a replacement number"); return; }
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/twilio/reassign", { user_id: replaceDlg.row.user_id, phone_number: pickNumber });
      toast.success(r.data.message || "Number replaced");
      setReplaceDlg(null); setPickNumber("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not replace number"); }
    finally { setBusy(false); }
  };

  const doRelease = async (row) => {
    if (!window.confirm(`Release ${row.phone_number} from ${row.customer_name || "this customer"} back into the available pool? Their AI receptionist will stop taking calls on this number.`)) return;
    try {
      const r = await adminApi.post("/admin/twilio/release-number", { user_id: row.user_id });
      toast.success(r.data.message || "Number released");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not release number"); }
  };

  const showAvailable = tab === "all" || tab === "available";
  const showAssigned = tab === "all" || tab === "assigned";

  return (
    <div data-testid="admin-twilio-numbers-view">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><PhoneCall className="h-6 w-6 text-blue-600" /> Twilio Number Management</h1>
          <p className="text-slate-500 mt-1">Every number in OperaMind's Twilio account. Assign, replace or release is an <b>emergency admin override</b> — it never changes normal customer onboarding.</p>
        </div>
        <Button data-testid="twilio-refresh" variant="outline" onClick={load} disabled={loading} className="gap-2"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
      </div>

      {!data.configured && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Twilio credentials are not connected — the available pool can't be listed.</div>
      )}

      <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard testid="stat-available" label="Available" value={data.available.length} icon={Phone} accent="emerald" sub="Not assigned to a customer" />
        <StatCard testid="stat-assigned" label="Assigned" value={data.assigned.length} icon={UserRound} accent="blue" sub="Currently in use" />
        <StatCard testid="stat-live" label="Live" value={data.assigned.filter((a) => a.live).length} icon={CheckCircle2} accent="violet" sub="Connected to a live AI" />
      </div>

      <div className="mt-5 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="twilio-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search number, name, customer or country…" className="pl-9" />
        </div>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          {["all", "available", "assigned"].map((t) => (
            <button key={t} data-testid={`twilio-tab-${t}`} onClick={() => setTab(t)}
              className={`px-3 py-2 text-sm font-medium capitalize ${tab === t ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{t}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : (
        <div className="mt-5 space-y-8">
          {showAvailable && (
            <section>
              <h2 className="text-base font-semibold text-slate-800 mb-2 flex items-center gap-2"><Phone className="h-4 w-4 text-emerald-600" /> Available Numbers <span className="text-slate-400 font-normal">({available.length})</span></h2>
              <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left font-semibold px-4 py-3">Number</th>
                      <th className="text-left font-semibold px-4 py-3">Friendly Name</th>
                      <th className="text-left font-semibold px-4 py-3">Country</th>
                      <th className="text-left font-semibold px-4 py-3">Capabilities</th>
                      <th className="text-left font-semibold px-4 py-3">Source</th>
                      <th className="text-right font-semibold px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {available.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No available numbers in the pool.</td></tr>}
                    {available.map((r) => (
                      <tr key={r.phone_number} data-testid={`available-row-${r.phone_number}`} className="hover:bg-emerald-50/30">
                        <td className="px-4 py-3 font-mono font-semibold text-slate-800">{r.phone_number}</td>
                        <td className="px-4 py-3 text-slate-600">{r.friendly_name || "—"}</td>
                        <td className="px-4 py-3 text-slate-600">{r.country || "—"}</td>
                        <td className="px-4 py-3"><Caps caps={r.capabilities} /></td>
                        <td className="px-4 py-3 text-slate-400 text-xs capitalize">{r.source || "—"}</td>
                        <td className="px-4 py-3 text-right">
                          <Button data-testid={`assign-btn-${r.phone_number}`} size="sm" className="gap-1 bg-blue-600 hover:bg-blue-700" onClick={() => { setAssignDlg({ number: r.phone_number }); setPickCustomer(""); setCustQ(""); }}>
                            <PhoneForwarded className="h-3.5 w-3.5" /> Assign
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {showAssigned && (
            <section>
              <h2 className="text-base font-semibold text-slate-800 mb-2 flex items-center gap-2"><UserRound className="h-4 w-4 text-blue-600" /> Assigned Numbers <span className="text-slate-400 font-normal">({assigned.length})</span></h2>
              <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left font-semibold px-4 py-3">Number</th>
                      <th className="text-left font-semibold px-4 py-3">Country</th>
                      <th className="text-left font-semibold px-4 py-3">Capabilities</th>
                      <th className="text-left font-semibold px-4 py-3">Customer</th>
                      <th className="text-left font-semibold px-4 py-3">Assigned</th>
                      <th className="text-left font-semibold px-4 py-3">AI</th>
                      <th className="text-right font-semibold px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {assigned.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-400">No assigned numbers match.</td></tr>}
                    {assigned.map((r) => (
                      <tr key={r.phone_number} data-testid={`assigned-row-${r.phone_number}`} className="hover:bg-blue-50/30">
                        <td className="px-4 py-3 font-mono font-semibold text-slate-800">{r.phone_number}</td>
                        <td className="px-4 py-3 text-slate-600">{r.country || "—"}</td>
                        <td className="px-4 py-3"><Caps caps={r.capabilities} /></td>
                        <td className="px-4 py-3">
                          <button data-testid={`view-customer-${r.user_id}`} onClick={() => navigate(`/admin/customers/${r.user_id}`)} className="text-left hover:underline">
                            <div className="font-medium text-slate-800">{r.customer_name || r.business_name || "—"}</div>
                            <div className="text-xs text-slate-400">{r.customer_email}</div>
                          </button>
                        </td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(r.date_assigned)}</td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.vapi_connected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{r.vapi_connected ? "Connected" : "Not linked"}</span>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <Button data-testid={`replace-btn-${r.user_id}`} size="sm" variant="outline" className="gap-1 mr-1" onClick={() => { setReplaceDlg({ row: r }); setPickNumber(""); }}><PhoneForwarded className="h-3.5 w-3.5" /> Replace</Button>
                          <Button data-testid={`release-btn-${r.user_id}`} size="sm" variant="outline" className="gap-1 text-rose-600 hover:text-rose-700" onClick={() => doRelease(r)}><Unplug className="h-3.5 w-3.5" /> Release</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}

      {/* Assign available number -> customer */}
      <Dialog open={!!assignDlg} onOpenChange={(o) => !o && setAssignDlg(null)}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign {assignDlg?.number}</DialogTitle>
            <DialogDescription>Emergency override — points a customer at this number, re-wires their AI, and writes an audit log. Any number they currently use is returned to the available pool.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="assign-customer-search" value={custQ} onChange={(e) => setCustQ(e.target.value)} placeholder="Search customer by name, email or business…" className="pl-9" />
          </div>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
            {filteredCustomers.length === 0 && <div className="py-6 text-center text-sm text-slate-400">No customers found.</div>}
            {filteredCustomers.map((c) => (
              <button key={c.user_id} data-testid={`assign-pick-${c.user_id}`} onClick={() => setPickCustomer(c.user_id)}
                className={`w-full text-left px-3 py-2 flex items-center justify-between ${pickCustomer === c.user_id ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                <div>
                  <div className="text-sm font-medium text-slate-800">{c.name || c.business_name || "Unnamed"}</div>
                  <div className="text-xs text-slate-400">{c.email} {c.phone_number ? `· current: ${c.phone_number}` : ""}</div>
                </div>
                {pickCustomer === c.user_id && <CheckCircle2 className="h-4 w-4 text-blue-600" />}
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDlg(null)}>Cancel</Button>
            <Button data-testid="assign-confirm" onClick={doAssign} disabled={busy || !pickCustomer} className="gap-2 bg-blue-600 hover:bg-blue-700">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Assign number</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace an assigned customer's number */}
      <Dialog open={!!replaceDlg} onOpenChange={(o) => !o && setReplaceDlg(null)}>
        <DialogContent data-testid="replace-dialog">
          <DialogHeader>
            <DialogTitle>Replace number for {replaceDlg?.row?.customer_name || "customer"}</DialogTitle>
            <DialogDescription>Current number <b>{replaceDlg?.row?.phone_number}</b> will return to the available pool and the chosen number will be re-wired to their AI. This is an emergency override.</DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
            {data.available.length === 0 && <div className="py-6 text-center text-sm text-slate-400">No available numbers to assign.</div>}
            {data.available.map((n) => (
              <button key={n.phone_number} data-testid={`replace-pick-${n.phone_number}`} onClick={() => setPickNumber(n.phone_number)}
                className={`w-full text-left px-3 py-2 flex items-center justify-between ${pickNumber === n.phone_number ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                <div>
                  <div className="text-sm font-mono font-semibold text-slate-800">{n.phone_number}</div>
                  <div className="text-xs text-slate-400">{n.friendly_name || n.country || ""}</div>
                </div>
                {pickNumber === n.phone_number && <CheckCircle2 className="h-4 w-4 text-blue-600" />}
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplaceDlg(null)}>Cancel</Button>
            <Button data-testid="replace-confirm" onClick={doReplace} disabled={busy || !pickNumber} className="gap-2 bg-blue-600 hover:bg-blue-700">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Replace number</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
